import { enrichGoogleAnalyticsSuffix } from 'components/shared/utils/enrichGoogleAnalyticsSuffix';
import { CURRENCY } from 'global.enums';

const sendGoogleEvent = (event: Record<string, unknown>) => {
  if (!Array.isArray(window.dataLayer)) return;

  window.dataLayer.push(event);
};

export const pushTicketStartEvent = (): void => {
  sendGoogleEvent({
    event: 'ticketStart',
  });
};

export const pushTicketSubmittedEvent = (): void => {
  sendGoogleEvent({
    event: 'ticketSubmitted',
  });
};

export const pushTicketErrorEvent = (errorMsg: string): void => {
  sendGoogleEvent({
    errorMsg,
    event: 'ticketError',
  });
};

export const pushLoginSuccessfulEvent = (userIdHash: string): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    event: 'loginSuccessful',
  });
};

export const pushLoginErrorEvent = (errorMsg: string): void => {
  sendGoogleEvent({
    errorMsg,
    event: 'loginError',
  });
};

export const pushRegistrationSuccessfulEvent = (
  userIdHash: string,
  clientSourceType: number,
  language: string,
  countryCode: string,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    language,
    countryCode,
    event: 'registrationSuccessful',
  });
};

export const pushRegistrationErrorEvent = (errorMsg: string): void => {
  sendGoogleEvent({
    errorMsg,
    event: 'registrationError',
  });
};

export const pushLanguageSelectorEvent = (language: string): void => {
  sendGoogleEvent({
    language,
    event: 'languageSelector',
  });
};

export const pushExportEvent = (userIdHash: string, event: string): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    event,
  });
};

export const pushAccountsBalanceCurrencyCodeEvent = (currencyCode: string): void => {
  sendGoogleEvent({
    currencyCode,
    event: 'accountsBalanceCurrencyCode',
  });
};

export const pushEWalletCurrencyCodeEvent = (currencyCode: string): void => {
  sendGoogleEvent({
    currencyCode,
    event: 'eWalletCurrencyCode',
  });
};

export const pushAccountCreateErrorEvent = (errorMsg: string): void => {
  sendGoogleEvent({
    errorMsg,
    event: 'accountCreateError',
  });
};

export const pushDownloadLegalDocumentEvent = (
  userIdHash: string,
  language: string,
  docType: string,
  event: string,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    language,
    docType,
    event,
  });
};

export const pushTradingPlatformDownloadEvent = (linkType: string): void => {
  sendGoogleEvent({
    platformDownloaded: linkType,
    event: 'tradingPlatformDownload',
  });
};

export const pushMamDocUploadEvent = (userIdHash: string, docType: string): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    docType,
    event: 'mamDocUpload',
  });
};

export const pushNotificationsFilterEvent = (subject: string): void => {
  sendGoogleEvent({
    subject,
    event: 'notificationsFilter',
  });
};

export const pushPartnershipStartEvent = (): void => {
  sendGoogleEvent({
    event: 'partnershipStart',
  });
};

export const pushPartnershipSubmittedEvent = (): void => {
  sendGoogleEvent({
    event: 'partnershipSubmitted',
  });
};

export const pushPartnershipErrorEvent = (userIdHash: string, errorMsg: string): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    errorMsg,
    event: 'partnershipError',
  });
};

export const pushDocumentUploadEvent = (documentType: string): void => {
  sendGoogleEvent({
    documentType,
    event: 'documentUpload',
  });
};

export const pushDocumentUploadErrorEvent = (errorMsg: string): void => {
  sendGoogleEvent({
    errorMsg,
    event: 'documentUploadError',
  });
};

export const pushPinPasswordUpdateEvent = (): void => {
  sendGoogleEvent({
    event: 'pinPasswordUpdate',
  });
};

export const pushEWalletViewedEvent = (): void => {
  sendGoogleEvent({
    event: 'eWalletViewed',
  });
};

export const pushTransferSuccessfulEvent = (
  accountIdHash: string | undefined,
  transferValue: string,
  transferCurrency: string,
  destinationAccountID: string | undefined,
): void => {
  sendGoogleEvent({
    accountID: enrichGoogleAnalyticsSuffix(accountIdHash),
    transferValue,
    transferCurrency,
    destinationAccountID,
    event: 'transferSuccessful',
  });
};

export const pushTransferErrorEvent = (accountIdHash: string | undefined, errorMsg: string): void => {
  sendGoogleEvent({
    accountID: enrichGoogleAnalyticsSuffix(accountIdHash),
    errorMsg,
    event: 'transferError',
  });
};

export const pushWithdrawFundsSuccessfulEvent = (
  accountIdHash: string,
  withdrawIdHash: string,
  depositValue: number,
  depositCurrency: string,
  depositSource: string,
): void => {
  sendGoogleEvent({
    accountID: enrichGoogleAnalyticsSuffix(accountIdHash),
    withdrawID: enrichGoogleAnalyticsSuffix(withdrawIdHash),
    depositValue,
    depositCurrency,
    depositSource,
    event: 'withdrawFundsSuccessful',
  });
};

export const pushWithdrawFundsErrorEvent = (accountIdHash: string, errorMsg: string): void => {
  sendGoogleEvent({
    accountID: enrichGoogleAnalyticsSuffix(accountIdHash),
    errorMsg,
    event: 'withdrawFundsError',
  });
};

export const pushAccountCreateSuccessfulEvent = (
  userIdHash: string,
  clientSourceType: number,
  accountIdHash: string,
  accountType: string,
  status: string,
  leverage?: number | string,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    accountID: enrichGoogleAnalyticsSuffix(accountIdHash),
    accountType,
    leverage,
    verification: status,
    event: 'accountCreateSuccessful',
  });
};

export const pushStartedCopyMasterSuccessEvent = (
  userIdHash: string,
  clientSourceType: number,
  accountIdHash: string,
  accountType: string,
  masterTradingAccountIdHash: string,
  masterUserIdHash: string,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    accountID: enrichGoogleAnalyticsSuffix(accountIdHash),
    accountType,
    master_accountID: enrichGoogleAnalyticsSuffix(masterTradingAccountIdHash),
    master_userId: enrichGoogleAnalyticsSuffix(masterUserIdHash),
    event: 'started_copy_master_success',
  });
};

export const pushStoppedCopyMasterSuccessEvent = (
  userIdHash: string,
  clientSourceType: number,
  accountIdHash: string,
  accountType: string,
  masterTradingAccountIdHash: string | undefined,
  masterUserIdHash: string | undefined,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    accountID: enrichGoogleAnalyticsSuffix(accountIdHash),
    accountType,
    master_accountID: enrichGoogleAnalyticsSuffix(masterTradingAccountIdHash),
    master_userId: enrichGoogleAnalyticsSuffix(masterUserIdHash),
    stop_copy_reason: 'byhimself',
    event: 'stopped_copy_master_success',
  });
};

export const pushHowToDepositVideoEvent = (
  userIdHash: string,
  clientSourceType: number,
  ibUserId: string | null,
  verification: string,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    ib_user_id: enrichGoogleAnalyticsSuffix(ibUserId),
    verification,
    eventAction: 'click_play',
    event: 'how_to_deposit_video',
  });
};

export const pushSuccessDepositEvent = (
  accountIdHash: string,
  depositIdHash: string,
  depositValue: number,
  depositAmountUSD: number,
  depositCurrency: CURRENCY,
  depositSource: string,
  userIdHash: string,
  clientSourceType: number,
  ibUserId: string | null,
  accountType: string,
  depositFundsType: string,
  verification: string,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    ib_user_id: enrichGoogleAnalyticsSuffix(ibUserId),
    accountID: enrichGoogleAnalyticsSuffix(accountIdHash),
    accountType,
    depositID: enrichGoogleAnalyticsSuffix(depositIdHash),
    depositValue,
    depositAmountUSD,
    depositCurrency,
    depositSource,
    deposit_funds_type: depositFundsType,
    verification,
    event: 'deposit_start',
  });
};

export const pushErrorDepositEvent = (accountIdHash: string, errorMsg: string): void => {
  sendGoogleEvent({
    accountID: enrichGoogleAnalyticsSuffix(accountIdHash),
    errorMsg,
    event: 'deposit_start_error',
  });
};

export const pushDepositPreStartEvent = (
  userIdHash: string,
  clientSourceType: number,
  ibUserId: string | null,
  accountIdHash: string,
  accountType: string,
  depositValue: number,
  depositCurrency: CURRENCY,
  depositAmountUSD: number,
  depositSource: string,
  depositFundsType: string,
  verification: string,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    ib_user_id: enrichGoogleAnalyticsSuffix(ibUserId),
    accountID: enrichGoogleAnalyticsSuffix(accountIdHash),
    accountType,
    depositValue,
    depositCurrency,
    depositAmountUSD,
    depositSource,
    deposit_funds_type: depositFundsType,
    verification,
    event: 'deposit_pre_start',
  });
};

export const pushQuickDepositClickBtnEvent = (
  userIdHash: string,
  clientSourceType: number,
  ibUserId: string | null,
  verification: string,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    ib_user_id: enrichGoogleAnalyticsSuffix(ibUserId),
    verification,
    event: 'quick_deposit_clck_btn',
  });
};

export const pushAccountCardDepositClickEvent = (
  userIdHash: string,
  clientSourceType: number,
  ibUserId: string | null,
  accountIdHash: string,
  accountType: string,
  verification: string,
  event: string,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    ib_user_id: enrichGoogleAnalyticsSuffix(ibUserId),
    accountID: enrichGoogleAnalyticsSuffix(accountIdHash),
    accountType,
    verification,
    event,
  });
};

export const pushSelectDepositFundsCategoryEvent = (
  userIdHash: string,
  clientSourceType: number,
  ibUserId: string | null,
  verification: string,
  depositFundsType: string | undefined,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    ib_user_id: enrichGoogleAnalyticsSuffix(ibUserId),
    verification,
    deposit_funds_type: depositFundsType,
    event: 'deposit_funds_type_start',
  });
};

export const pushDepositPaymentSourceEvent = (
  userIdHash: string,
  clientSourceType: number,
  ibUserId: string | null,
  verification: string,
  depositFundsType: string | undefined,
  depositSource: string,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    ib_user_id: enrichGoogleAnalyticsSuffix(ibUserId),
    verification,
    deposit_funds_type: depositFundsType,
    depositSource,
    eventAction: 'psp_click',
    event: 'deposit_payment_source',
  });
};

export const pushAuthorizedBankingMethodEvent = (
  userIdHash: string,
  clientSourceType: number,
  ibUserId: string | null,
  depositFundsType: string | undefined,
  verification: string,
  eventAction: string,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    ib_user_id: enrichGoogleAnalyticsSuffix(ibUserId),
    verification,
    deposit_funds_type: depositFundsType,
    eventAction,
    event: 'online_bank_transaction',
  });
};

export const pushOpenAccountModalShowEvent = (
  userIdHash: string,
  clientSourceType: number,
  verification: string,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    verification,
    event: 'open_account_modal_window_shows',
  });
};

export const pushOpenAccountModalCloseEvent = (
  userIdHash: string,
  clientSourceType: number,
  verification: string,
): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    verification,
    event: 'open_account_modal_window_closed',
  });
};

export const pushRegistrationSuccessfulMaPageEvent = (userIdHash: string, clientSourceType: number): void => {
  sendGoogleEvent({
    userID: enrichGoogleAnalyticsSuffix(userIdHash),
    clientSourceType,
    event: 'registration_successful_ma_page',
  });
};

export const pushGetSmsCodeEvent = (): void => {
  sendGoogleEvent({
    event: 'sms_phone_confirm_click_get_code',
  });
};

export const pushInputSmsCodeCharacterEvent = (): void => {
  sendGoogleEvent({
    event: 'sms_phone_confirm_input_character',
  });
};

export const pushSmsCodeResendEvent = (): void => {
  sendGoogleEvent({
    event: 'sms_phone_confirm_resend_code',
  });
};

export const pushSmsCodeIsCorrectEvent = (): void => {
  sendGoogleEvent({
    event: 'sms_phone_confirm_success',
  });
};

export const pushSmsCodeIsIncorrectEvent = (): void => {
  sendGoogleEvent({
    event: 'sms_phone_confirm_fail',
  });
};

export const pushRegistrationContinueEvent = (): void => {
  sendGoogleEvent({
    event: 'ma_reg_continue_button_click',
  });
};
